import React from "react";
import { Strategy } from "./InvestmentStrategyContext";
import StrategyPieChart from "./StrategyPieChart";

const ASSET_COLORS = {
  stocks: "#1A2B4C",    // Navy
  bonds: "#2D4C7F",     // Blue
  cash: "#c4a052",      // Gold
  alts: "#9F8E63"       // Lion
} as const;

interface StrategyContentProps {
  strategy: Strategy;
  onTotalChange?: (total: number) => void;
}

const StrategyContent = ({ strategy, onTotalChange }: StrategyContentProps) => {
  const chartData = [
    { name: "Stocks", value: strategy.allocation.stocks, color: ASSET_COLORS.stocks },
    { name: "Bonds", value: strategy.allocation.bonds, color: ASSET_COLORS.bonds },
    { name: "Cash", value: strategy.allocation.cash, color: ASSET_COLORS.cash },
    { name: "Alternatives", value: strategy.allocation.alts, color: ASSET_COLORS.alts }
  ].filter(item => item.value > 0);

  // Calculate total and notify parent
  React.useEffect(() => {
    if (onTotalChange) {
      const total = Object.values(strategy.allocation).reduce((sum, value) => sum + value, 0);
      onTotalChange(total);
    }
  }, [strategy.allocation, onTotalChange]);

  return (
    <div className="space-y-8">
      {/* Title and Objective Section */}
      <div className="space-y-3">
        <h3 className="text-xl font-semibold text-navy">{strategy.title}</h3>
        <div className="flex gap-2">
          <span className="font-medium text-navy">Objective:</span>
          <p className="text-navy-light">{strategy.description}</p>
        </div>
      </div>

      {/* Chart Section */}
      <div className="w-full max-w-md mx-auto">
        <StrategyPieChart data={chartData} />
      </div>

      {/* Investor Profile Section */}
      <div className="space-y-4">
        <h4 className="font-medium text-navy">
          Typically, an investor seeking {strategy.label.toLowerCase()} is:
        </h4>
        <ul className="space-y-3">
          {strategy.points.map((point, index) => (
            <li key={index} className="flex gap-2 text-navy-light">
              <span>•</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default StrategyContent;